import { CSSProperties, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { cx } from 'class-variance-authority';
import { useTenantCreationRequest } from '@/hooks/use-tenant-creation-request';
import { ONBOARDING_PLATFORM_OPTIONS } from '@/lib/onboarding-platform-options';

// Breadcrumb of the onboarding flow's own steps, shown at the top of the
// left column — not a generic 1-of-5 progress bar, but each already-answered
// step rendered as its actual answer (platform name, app name, tab count,
// ...), so the lead can see what they've already told us at a glance and
// jump back to fix it. Reads from the *persisted* TenantCreationRequest
// (not OnboardingPreviewState) on purpose: a step only counts as "answered"
// here once its value has survived a PATCH, not while it's still being
// typed/dragged on the current step.
export type OnboardingStepId =
  | 'choose-store'
  | 'app-name-link'
  | 'app-logo'
  | 'tab-bar'
  | 'notification';

// Route segments double as step ids — each is navigable via `../<id>`
// exactly as the step pages themselves already navigate to one another.
const STEP_ORDER: OnboardingStepId[] = [
  'choose-store',
  'app-name-link',
  'app-logo',
  'tab-bar',
  'notification',
];

const STEP_LABELS: Record<OnboardingStepId, string> = {
  'choose-store': 'Loja',
  'app-name-link': 'Nome do app',
  'app-logo': 'Logo',
  'tab-bar': 'Navegação',
  notification: 'Notificação',
};

const completedChipClassName =
  'flex shrink-0 items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/40 hover:bg-primary/10';

export interface OnboardingStepsIndicatorProps {
  currentStep: OnboardingStepId;
  className?: string;
}

export function OnboardingStepsIndicator({
  currentStep,
  className,
}: OnboardingStepsIndicatorProps) {
  const navigate = useNavigate();
  const tcr = useTenantCreationRequest();
  const currentIndex = STEP_ORDER.indexOf(currentStep);
  const currentChipRef = useRef<HTMLSpanElement>(null);

  // On narrow screens the chips overflow horizontally — keep the current
  // step visible instead of leaving it off-screen. Re-runs when a chip image
  // (the platform logo) finishes loading, since that changes chip widths.
  useEffect(() => {
    const chip = currentChipRef.current;
    const container = chip?.parentElement;
    if (!chip || !container) return;

    const scrollToCurrent = () =>
      chip.scrollIntoView({ inline: 'center', block: 'nearest' });

    scrollToCurrent();
    container.addEventListener('load', scrollToCurrent, true);
    return () => container.removeEventListener('load', scrollToCurrent, true);
  }, [currentStep]);

  const goToStep = (step: OnboardingStepId) =>
    navigate(`../${step}`, { viewTransition: true });

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cx(
        'no-scrollbar mb-6 flex flex-nowrap items-center gap-1.5 overflow-x-auto [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] lg:[mask-image:none]',
        className
      )}
    >
      {STEP_ORDER.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = step === currentStep;

        if (isCompleted) {
          // Step 1 gets the chosen platform's own branding (logo + color)
          // instead of the generic check+label treatment — it's the one
          // answer with a recognizable visual identity of its own, so
          // showing it as plain text would throw that away.
          if (step === 'choose-store') {
            const platform = ONBOARDING_PLATFORM_OPTIONS.find(
              (option) => option.id === tcr.storePlatform
            );

            if (platform) {
              const chipStyle: CSSProperties = {
                borderColor: `${platform.color}40`,
                backgroundColor:
                  platform.cardBackground ?? `${platform.color}14`,
              };

              return (
                <button
                  key={step}
                  type="button"
                  aria-label={`Loja: ${platform.label}. Editar.`}
                  onClick={() => goToStep(step)}
                  style={chipStyle}
                  className="flex shrink-0 items-center rounded-full border px-3 py-1 transition-opacity hover:opacity-80"
                >
                  <img
                    src={platform.logo}
                    alt={platform.label}
                    className="h-3.5 max-w-[70px] object-contain"
                  />
                </button>
              );
            }
          }

          const value = answerForStep(step, tcr);

          return (
            <button
              key={step}
              type="button"
              onClick={() => goToStep(step)}
              className={completedChipClassName}
            >
              <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
              <span className="max-w-[108px] truncate">
                {value ?? STEP_LABELS[step]}
              </span>
            </button>
          );
        }

        return (
          <span
            key={step}
            ref={isCurrent ? currentChipRef : undefined}
            className={cx(
              'flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
              isCurrent
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/60 text-muted-foreground'
            )}
          >
            {STEP_LABELS[step]}
          </span>
        );
      })}
      {/* Lets the last chip scroll clear of the mobile edge fade. */}
      <span aria-hidden className="w-6 shrink-0 lg:hidden" />
    </motion.div>
  );
}

function answerForStep(
  step: OnboardingStepId,
  tcr: ReturnType<typeof useTenantCreationRequest>
): string | null {
  switch (step) {
    case 'choose-store':
      return (
        ONBOARDING_PLATFORM_OPTIONS.find(
          (option) => option.id === tcr.storePlatform
        )?.label ?? null
      );
    case 'app-name-link':
      return tcr.appName;
    case 'app-logo':
      return null;
    case 'tab-bar':
      return tcr.tabs && tcr.tabs.length > 0
        ? `${tcr.tabs.length} ${tcr.tabs.length === 1 ? 'aba' : 'abas'}`
        : null;
    case 'notification':
      return tcr.notificationPreviewTitle;
    default:
      return null;
  }
}
